/**
 * Crew project screen (D-3/A3) — the crew's view of a PROJECT they work
 * directly, no task needed. The counterpart of the tabbed task screen and built
 * on the same shell: Back bar, header, sticky segmented tabs.
 *
 *   Project  — Start / Finish (D-10: on_site reads "In progress"; Finish →
 *              pending_closeout, undoable within the grace), site + directions,
 *              who's on it, and the project's status timeline
 *   Files    — plans + documents (project-scoped, shared with the task screen)
 *   Recap    — recap notes capture (project-scoped)
 *
 * Chat lands here in D1 (chat moves from the task to the project).
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock,
  Loader2,
  MapPin,
  Navigation,
  Phone,
  Play,
  RotateCcw,
  Star,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import { phoneToTelHref, type Project, type ProjectStatusEvent } from '@vari-crew/schemas';
import { useProject } from '@/api/projects';
import { useProjectAssignments } from '@/api/project-assignments';
import { isQueuedProjectResult, useProjectStatusEvents, useTransitionProject } from '@/api/project-transitions';
import { useChatMessages } from '@/api/chat';
import { TaskChatPanel } from '@/components/TaskChatPanel';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { RecapNotesCard } from '@/components/projects/RecapNotesCard';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { CopyButton } from '@/components/ui/CopyButton';
import { SiteMap } from '@/components/ui/SiteMap';
import { cn } from '@/lib/cn';
import {
  CREW_PROJECT_ACTIVE,
  CREW_PROJECT_STATUS_LABEL,
  PROJECT_FINISH_UNDO_GRACE_MS,
  crewCanFinish,
  crewCanStart,
} from '@/lib/projectStatusCrew';
import { BackBar, PlansCard, TaskDocumentsCard } from './CrewTaskDetail';

type TabKey = 'project' | 'files' | 'chat' | 'recap';

function tabFromHash(hash: string): TabKey | null {
  const map: Record<string, TabKey> = {
    '#files': 'files',
    '#chat': 'chat',
    // `#crew-chat` is the mention-email deep link (kept for back-compat).
    '#crew-chat': 'chat',
    '#recap': 'recap',
    '#project': 'project',
  };
  return map[hash] ?? null;
}

function timeOnly(iso: string): string {
  return format(parseISO(iso), 'h:mm a');
}
function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}
/** Re-render once a minute while something is counting or in its grace window. */
function useNowTicker(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}
function directionsUrl(addr: {
  line1: string;
  city: string;
  state: string;
  postal_code: string;
  latitude: number | null;
  longitude: number | null;
}): string {
  const dest =
    addr.latitude != null && addr.longitude != null
      ? `${addr.latitude},${addr.longitude}`
      : `${addr.line1}, ${addr.city}, ${addr.state} ${addr.postal_code}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
}

export function CrewProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: project, isLoading, error } = useProject(projectId);
  const { data: me } = useCurrentUser();
  const canManage = me?.role === 'supervisor' || me?.role === 'admin';
  const canAct = me?.role === 'crew' || canManage;
  const [tab, setTab] = useState<TabKey>(() => tabFromHash(location.hash) ?? 'project');

  const goBack = () => (location.key !== 'default' ? navigate(-1) : navigate('/today'));
  useEffect(() => {
    const t = tabFromHash(location.hash);
    if (t) setTab(t);
  }, [location.hash]);

  // Project-chat "new message" dot: poll the thread (shared cache with the
  // panel) even off the Chat tab, and light a dot when a teammate's newer
  // message lands, until the tab is opened. Marker is per-PROJECT now (D1).
  const chatQuery = useChatMessages(canAct && projectId ? { projectId } : undefined);
  const latestMsg = chatQuery.data?.data.at(-1);
  const seenKey = `vari-crew.chat-seen.project.${projectId ?? ''}`;
  const [chatSeenAt, setChatSeenAt] = useState<string | null>(() => {
    try {
      return localStorage.getItem(seenKey);
    } catch {
      return null;
    }
  });
  const chatUnseen =
    !!latestMsg && latestMsg.author_user_id !== me?.id && (!chatSeenAt || latestMsg.created_at > chatSeenAt);
  useEffect(() => {
    if (tab === 'chat' && latestMsg && latestMsg.created_at !== chatSeenAt) {
      try {
        localStorage.setItem(seenKey, latestMsg.created_at);
      } catch {
        /* storage blocked */
      }
      setChatSeenAt(latestMsg.created_at);
    }
  }, [tab, latestMsg, chatSeenAt, seenKey]);

  const tabs = useMemo(
    () =>
      [
        { key: 'project' as const, label: 'Project', show: true },
        { key: 'files' as const, label: 'Files', show: true },
        { key: 'chat' as const, label: 'Chat', show: canAct },
        { key: 'recap' as const, label: 'Recap', show: canAct },
      ].filter((t) => t.show),
    [canAct],
  );

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center bg-fill-soft">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error || !project) {
    return (
      <div className="min-h-full bg-fill-soft">
        <div className="max-w-md md:max-w-2xl mx-auto px-4 pt-4">
          <BackBar onBack={goBack} />
          <div className="bg-bg rounded-2xl border border-line p-8 text-center text-sm text-ink-dim mt-4">
            {(error as Error | undefined)?.message ?? 'This project could not be found.'}
          </div>
        </div>
      </div>
    );
  }

  const active = tabs.some((t) => t.key === tab) ? tab : 'project';

  return (
    <div className="min-h-full bg-fill-soft pb-10">
      <div className="max-w-md md:max-w-2xl mx-auto px-4 pt-4 space-y-4">
        <BackBar onBack={goBack} />
        <ProjectHeader project={project} />

        <div className="sticky top-0 z-10 -mx-1 px-1 pt-1 bg-fill-soft">
          <div
            role="tablist"
            aria-label="Project sections"
            className="grid rounded-xl bg-fill p-1 border border-line"
            style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
          >
            {tabs.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={active === t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  'relative rounded-lg py-1.5 text-[13px] font-medium transition-colors',
                  active === t.key ? 'bg-bg text-ink shadow-sm' : 'text-ink-dim hover:text-ink',
                )}
              >
                {t.label}
                {t.key === 'chat' && chatUnseen && (
                  <span
                    aria-label="New messages"
                    className="absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full bg-accent"
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {active === 'project' && (
          <div className="space-y-4">
            <ProjectTimerCard project={project} canAct={canAct} />
            <ProjectDirectivesCard project={project} />
            <ProjectSiteCard project={project} />
            <ProjectCrewCard projectId={project.id} />
            <ProjectTimelineCard projectId={project.id} />
          </div>
        )}
        {active === 'files' && (
          <div className="space-y-4">
            <PlansCard projectId={project.id} />
            <TaskDocumentsCard projectId={project.id} />
          </div>
        )}
        {active === 'chat' && canAct && <TaskChatPanel scope={{ projectId: project.id }} />}
        {active === 'recap' && canAct && (
          <RecapNotesCard projectId={project.id} variant="capture" audience="Goes in the closeout recap" />
        )}

        {canManage && (
          <button
            onClick={() => navigate(`/projects/${project.id}`)}
            className="flex items-center justify-between w-full bg-bg rounded-2xl border border-line p-4 text-sm text-ink-dim hover:bg-fill-soft"
          >
            <span>Open full project · {project.client_name}</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function ProjectHeader({ project }: { project: Project }) {
  const isActive = CREW_PROJECT_ACTIVE.has(project.status);
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-ink-faint">Project</div>
      <h1 className="text-xl font-semibold leading-tight mt-0.5">{project.title}</h1>
      <div className="text-sm text-ink-dim mt-0.5 flex items-center gap-2 flex-wrap">
        <span>{project.client_name}</span>
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
            isActive
              ? 'bg-status-onsite text-status-onsite-fg'
              : project.status === 'pending_closeout' || project.status === 'complete'
                ? 'bg-status-complete text-status-complete-fg'
                : 'bg-fill text-ink-dim',
          )}
        >
          {CREW_PROJECT_STATUS_LABEL[project.status]}
        </span>
      </div>
    </div>
  );
}

/** Start / Finish with a running clock — the project analogue of the task TimerCard. */
function ProjectTimerCard({ project, canAct }: { project: Project; canAct: boolean }) {
  const transition = useTransitionProject(project.id);
  const running = project.status === 'on_site' || project.status === 'punch';
  const finishedMs = project.finished_at ? parseISO(project.finished_at).getTime() : null;
  const now = useNowTicker(running || (finishedMs != null && Date.now() - finishedMs < PROJECT_FINISH_UNDO_GRACE_MS));
  const canUndo =
    project.status === 'pending_closeout' && finishedMs != null && now - finishedMs < PROJECT_FINISH_UNDO_GRACE_MS;

  let eyebrow: string;
  let big: string;
  if (running && project.started_at) {
    eyebrow = `Started ${timeOnly(project.started_at)}`;
    big = formatClock(now - parseISO(project.started_at).getTime());
  } else if (project.status === 'pending_closeout' && project.finished_at) {
    eyebrow = `Finished ${timeOnly(project.finished_at)}`;
    big = project.started_at
      ? formatClock(parseISO(project.finished_at).getTime() - parseISO(project.started_at).getTime())
      : '—';
  } else if (project.status === 'complete') {
    eyebrow = 'Closed out';
    big = '✓';
  } else {
    eyebrow = project.status === 'scheduled' ? 'Scheduled' : CREW_PROJECT_STATUS_LABEL[project.status];
    big = '—';
  }

  function go(to: 'on_site' | 'pending_closeout') {
    const said = to === 'on_site' ? 'in progress' : 'finished';
    transition.mutate(
      { to },
      {
        onSuccess: (res) =>
          isQueuedProjectResult(res)
            ? toast.success(`Saved offline — "${said}" will sync when you reconnect`)
            : toast.success(`Project marked ${said}`),
        onError: (err) => toast.error(`Failed: ${err.message}`),
      },
    );
  }

  return (
    <div
      className={cn(
        'rounded-2xl p-5 text-center',
        running ? 'bg-status-onsite text-status-onsite-fg' : 'bg-bg border border-line',
      )}
    >
      <div className="text-[11px] uppercase tracking-widest opacity-80">{eyebrow}</div>
      <div className="text-3xl font-bold tracking-tight tabular-nums mt-1">{big}</div>
      {canAct && (crewCanStart(project.status) || crewCanFinish(project.status) || canUndo) && (
        <div className="flex gap-2 mt-4">
          {crewCanStart(project.status) && (
            <Button size="sm" className="flex-1 h-11" onClick={() => go('on_site')} disabled={transition.isPending}>
              <Play className="w-3.5 h-3.5" /> Start
            </Button>
          )}
          {crewCanFinish(project.status) && (
            <Button size="sm" className="flex-1 h-11" onClick={() => go('pending_closeout')} disabled={transition.isPending}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Finish
            </Button>
          )}
          {canUndo && (
            <Button size="sm" variant="outline" className="flex-1 h-11" onClick={() => go('on_site')} disabled={transition.isPending}>
              <RotateCcw className="w-3.5 h-3.5" /> Undo finish
            </Button>
          )}
        </div>
      )}
      {project.status === 'pending_closeout' && (
        <div className="text-[11px] opacity-80 mt-3">Finished — your supervisor reviews the recap and closes it out.</div>
      )}
    </div>
  );
}

/**
 * Project details (D2) — the supervisor's directions for how to run this job,
 * read-only for the crew. Backed by `internal_notes` (crew-visible since D2 via
 * redactProjectFinancials). Stays hidden when there are none, so the screen
 * doesn't carry an empty box on the ~40% of projects without directives.
 */
function ProjectDirectivesCard({ project }: { project: Project }) {
  const details = project.internal_notes?.trim();
  if (!details) return null;
  return (
    <div className="bg-bg rounded-2xl border border-line p-4">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink-faint mb-2">
        <ClipboardList className="w-3.5 h-3.5" /> Project details
      </div>
      <p className="text-sm whitespace-pre-wrap">{details}</p>
    </div>
  );
}

function ProjectSiteCard({ project }: { project: Project }) {
  const addr = project.site_address;
  if (!addr && !project.site_contact_name) return null;
  return (
    <div className="bg-bg rounded-2xl border border-line p-4 space-y-3">
      {addr && (
        <div className="flex items-start justify-between gap-2 text-sm">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 mt-0.5 text-ink-faint flex-shrink-0" />
            <div>
              {addr.line1}
              {addr.line2 && <>, {addr.line2}</>}
              <br />
              {addr.city}, {addr.state} {addr.postal_code}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a
              href={directionsUrl(addr)}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-accent font-medium whitespace-nowrap"
            >
              <Navigation className="w-3.5 h-3.5" /> Directions
            </a>
            <CopyButton
              value={[addr.line1, addr.line2, `${addr.city}, ${addr.state} ${addr.postal_code}`].filter(Boolean).join(', ')}
              label="Copy address"
            />
          </div>
        </div>
      )}
      {project.site_contact_name && (
        <div className="flex items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2 text-ink-dim min-w-0">
            <User className="w-4 h-4 text-ink-faint shrink-0" />
            <span className="truncate">{project.site_contact_name}</span>
          </div>
          {project.site_contact_phone && (
            <a
              href={phoneToTelHref(project.site_contact_phone) ?? `tel:${project.site_contact_phone}`}
              className="flex items-center gap-1 text-accent font-medium tabular-nums shrink-0"
            >
              <Phone className="w-3.5 h-3.5" /> {project.site_contact_phone}
            </a>
          )}
        </div>
      )}
      {addr && (
        <SiteMap latitude={addr.latitude ?? null} longitude={addr.longitude ?? null} label="Project site" expandable className="h-40" />
      )}
    </div>
  );
}

function ProjectCrewCard({ projectId }: { projectId: string }) {
  const { data } = useProjectAssignments(projectId);
  const crew = data?.assignments ?? [];
  if (crew.length === 0) return null;
  return (
    <div className="bg-bg rounded-2xl border border-line p-4">
      <div className="text-[11px] uppercase tracking-widest text-ink-faint mb-2">On this project</div>
      <ul className="space-y-1.5">
        {crew.map((c) => (
          <li key={c.crew_member_id} className="flex items-center gap-2 text-sm">
            <Avatar name={c.display_name} src={c.avatar_url} className="w-7 h-7 text-[11px]" />
            <span className="truncate">{c.display_name}</span>
            <span className="text-ink-faint text-xs">· {c.crew_role}</span>
            {c.is_lead && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 font-medium ml-auto" title="Crew lead">
                <Star className="w-3 h-3 fill-amber-400" /> lead
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProjectTimelineCard({ projectId }: { projectId: string }) {
  const { data } = useProjectStatusEvents(projectId);
  const events: ProjectStatusEvent[] = data?.events ?? [];
  if (events.length === 0) return null;
  return (
    <div className="bg-bg rounded-2xl border border-line p-4">
      <div className="text-[11px] uppercase tracking-widest text-ink-faint mb-2">This project</div>
      <ul className="space-y-2">
        {events.map((e) => (
          <li key={e.id} className="flex items-start gap-2 text-sm">
            <Clock className="w-3.5 h-3.5 mt-0.5 text-ink-faint shrink-0" />
            <div className="min-w-0">
              <span className="font-medium">{CREW_PROJECT_STATUS_LABEL[e.to_status]}</span>
              <span className="text-ink-faint text-xs">
                {' · '}
                {format(parseISO(e.occurred_at), 'EEE h:mm a')}
                {e.actor_name && ` · ${e.actor_name}`}
              </span>
              {e.note && <div className="text-xs text-ink-dim mt-0.5 whitespace-pre-wrap">{e.note}</div>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
